import { Reveal } from "@components/ui";

interface StepCardProps {
  number: string;
  title: string;
  description: string;
  timeline: string;
  icon: React.ReactNode;
}

function StepCard({ number, title, description, timeline, icon }: StepCardProps) {
  return (
    <div className="group relative w-full rounded-[12px] md:rounded-[20px] bg-white overflow-hidden flex flex-col items-start p-5 md:p-7 lg:p-8 gap-5 md:gap-6 border border-transparent hover:border-[#e8e9ed] hover:shadow-[0_4px_20px_rgba(0,0,0,0.08)] transition-all duration-300">
      {/* Watermark number */}
      <span className="absolute top-4 right-5 md:top-5 md:right-6 text-[64px] md:text-[80px] font-bold leading-none text-[#122d63]/[0.04] select-none pointer-events-none">
        {number}
      </span>

      {/* Top row: number badge + icon */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 md:w-12 md:h-12 rounded-[12px] bg-[#122d63] group-hover:bg-[#f24d2b] flex items-center justify-center transition-colors duration-300">
          <span className="text-sm md:text-base font-semibold text-white font-albert-sans">
            {number}
          </span>
        </div>
        <div className="w-10 h-10 rounded-[12px] bg-[#f24d2b]/[0.08] text-[#f24d2b] flex items-center justify-center">
          {icon}
        </div>
      </div>

      {/* Content */}
      <div className="flex flex-col items-start gap-3 md:gap-4">
        <h3 className="text-sm md:text-lg lg:text-[24px] tracking-[-0.04em] font-semibold text-[#090d2c] font-albert-sans leading-[1.2]">
          {title}
        </h3>
        <p className="text-[14px] md:text-base lg:text-[18px] tracking-[-0.04em] text-[rgba(10,14,46,0.75)] font-albert-sans leading-[1.5]">
          {description}
        </p>
      </div>

      {/* Timeline badge */}
      <div className="mt-auto pt-2">
        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#122d63]/[0.06] text-[#122d63] text-[12px] md:text-[13px] font-medium font-albert-sans">
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M12 6v6l4 2" />
          </svg>
          {timeline}
        </span>
      </div>
    </div>
  );
}

const AcknowledgeIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);

const InvestigateIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const FollowUpIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const steps: StepCardProps[] = [
  {
    number: "01",
    title: "Acknowledgment",
    description:
      "Your feedback is received and assigned a unique reference number. You'll receive confirmation via email and SMS.",
    timeline: "Within 24 hours",
    icon: <AcknowledgeIcon />,
  },
  {
    number: "02",
    title: "Investigation & Resolution",
    description:
      "Our dedicated team reviews your feedback, investigates the matter, and works to resolve any issues identified.",
    timeline: "3–5 business days",
    icon: <InvestigateIcon />,
  },
  {
    number: "03",
    title: "Follow-Up & Closure",
    description:
      "We contact you with the resolution outcome and ensure your satisfaction before formally closing the case.",
    timeline: "Within 7 days",
    icon: <FollowUpIcon />,
  },
];

export function ResponseFollowUpSection() {
  return (
    <section className="w-full bg-[#f9f9fb] overflow-hidden py-12 md:py-16 lg:py-[100px] px-5 md:px-10 lg:px-[180px]">
      <div className="w-full max-w-[1368px] mx-auto flex flex-col items-center gap-10 lg:gap-16">
        {/* Badge + Title */}
        <Reveal direction="up">
          <div className="flex flex-col items-center gap-4 md:gap-5">
            <span className="inline-block px-4 py-1.5 rounded-full bg-[#122d63]/[0.08] text-[#122d63] text-[13px] md:text-[15px] font-semibold tracking-[-0.02em] uppercase">
              What Happens Next
            </span>
            <h2 className="text-center text-[24px] md:text-[40px] lg:text-[60px] tracking-[-0.04em] font-semibold text-[#122d63] font-albert-sans leading-[1.1]">
              Response & Follow-Up
            </h2>
            <p className="text-center max-w-[580px] text-[14px] md:text-base lg:text-[18px] text-[rgba(10,14,46,0.75)] font-albert-sans leading-[1.6]">
              Here's what to expect after you submit your feedback. We're
              committed to transparent communication at every step.
            </p>
          </div>
        </Reveal>

        {/* Step Cards Grid */}
        <div className="w-full grid grid-cols-1 lg:grid-cols-3 gap-5 md:gap-6">
          {steps.map((step, index) => (
            <Reveal key={step.number} direction="up" delay={index * 120}>
              <StepCard {...step} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
